import Home from "home/home";
import { Route, Routes, useLocation } from "react-router-dom";
import Bankroll from "components/bankroll";
import ProfilePage from "components/profile/profilePage.tsx";
import Drawer from "components/drawer";
import "rsuite/dist/rsuite.min.css";
import useDeviceDimensions from "hooks/useDeviceDimensions";
import SideBar from "components/sidebar";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { close } from "state/slices/drawer-slice";
import ForgotPassword from "components/forgot-password";
import SparketTutorial from "components/sparket-tutorial";
import LeaderboardPage from "components/leaderboard-page";
import LegalPage from "components/legal-page";
import ConfirmResults from "components/confirm-results";
import ConfirmResultsPoolDetails from "components/confirm-results/pool-details";
import {
  fetchGroupCustomization,
  getGroupSecondaryColorOrDefault,
  getGroupState,
  getGroupStyles,
  setIsContestGroup,
} from "state/slices/group-slice";
import ReactGA4 from "react-ga4";
import React, { useEffect } from "react";
import InitializeGroupState from "app-setup/initialize-group-state";
import InitializeGeolocation from "app-setup/initialize-geolocation";
import InitializeUserState from "app-setup/initialize-user-state";
import CheckGroupStatus from "app-setup/check-group-status";
import { useSpeedSessionHandshake } from "app-setup/use-speed-session-handshake";
import LegalModal from "components/legal-modal";
import ExternalIdModal from "components/external-id-form";
import LoginModal from "components/profile/login-modal";
import TwoFactorModal from "components/profile/two-factor-auth";
import {
  getPrivacyPolicyPath,
  getTermsOfServicePath,
  getUserPath,
  integratedUserPath,
} from "utils/backend-path-builders";
import ResponsibleGaming from "./components/responsible-gaming";
import UnsupportedBrowserAlert from "components/unsupported-browser-alert";
import OfflineAlert from "components/offline-alert";
import { fetchUser, getUserState, setSuperAdmin } from "state/slices/user-slice";
import { WagerWireIframe, WagerWireProvider } from "@wagerwire/embed/react";
import { useAuth } from "hooks/auth";
import ContestPopup from "components/contest-popup/modal";
import {
  getIntegratedAppState,
  IntegratedAppState,
  removeIntegratedState,
} from "state/slices/integrated-app-slice";
import { useConsoleCapture } from "hooks/useConsoleCapture";
import { useNetworkCapture } from "hooks/useNetworkCapture";
import NotificationSettings from "components/notification-settings";
import {
  APP_CONFIG_OVERRIDES_LOCAL_STORAGE_KEY,
  checkIfAppConfigResetIsRequired,
  defaultAppConfig,
  getAppConfigOverridesFromLocalStorage,
  getAppConfigOverridesFromQueryParams,
  setAppConfig,
} from "state/slices/app-config-slice";
import { INTEGRATED_APP_HOME_PAGE_URL_PREFIX } from "utils/integration/constants";
import { useIsIntegratedAppHomePage } from "utils/integration/integrated-app-tools";
import Help from "components/help";
import IdentityVerificationPage from "components/profile/identity-verification/identityVerificationPage.tsx";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus.ts";
import ContestPage from "./components/contests/contest-details";
import UserPage from "components/user/user-page.tsx";
import MyPicksPage from "components/my-bets/my-picks-page.tsx";
import PoolPage from "components/pool-details/pool-page.tsx";
import { fetchPaymentMethods } from "state/slices/user-payment-methods-slice.ts";
import { fetchRealWallet } from "state/slices/user-real-wallet-slice.ts";
import { fetchOrders } from "state/slices/user-orders-slice.ts";
import OrdersManager from "components/profile/orders/orders-manager.tsx";
import { fetchProducts } from "state/slices/products-slice.ts";
import { fetchBattlePasses } from "state/slices/battle-passes-slice.ts";
import ContestHome from "components/contests";
import { WithdrawFundsModal } from "components/commerce/withdrawal";
import DepositFundsModal from "components/commerce/v2/deposit-funds-modal.tsx";
import GlobalLoadingOverlay from "components/common/global-loading-overlay.tsx";
import DebugPage from "components/debug-page/debug-page";
import UiLibraryContestPage from "development/ui-library/contest/page";
import { getRequest } from "utils/httpClient.ts";
import UsersResponse from "interfaces/users-response.ts";
import PartnerGame from "components/partner-game/partner-game";
import PlaidOAuthPage from "components/payment-provider/plaid/plaid-oauth-page.tsx";
import {
  EDIT_PROFILE_PATH,
  IS_INTEGRATED_APP,
  SPEED_ACCOUNT_ID,
  sparketBlack,
} from "utils/constants";
import * as storage from "utils/local-storage";

ReactGA4.initialize("G-W4C48RRRY7");

const App = () => {
  const excludeNavigationPaths = [
    "/forgot-password",
    "/privacy-policy",
    "/terms-of-service",
  ];

  const excludeBackgroundColorPaths = [
    "/forgot-password",
    "/privacy-policy",
    "/terms-of-service",
    "/help",
    "/edit-profile",
    "/profile",
    "/bankroll",
    "/leaderboard",
    "/responsible-gaming",
    "/notification-settings",
    "/debug",
  ];

  const auth = useAuth();
  const user = useAppSelector(getUserState);
  const group = useAppSelector(getGroupState);
  const location = useLocation();

  // Speed Wallet passes `acct` / `p_add` / `lang` / `bal_*` as URL params at
  // iframe mount. Capture them into sessionStorage here so it works regardless
  // of which route Speed opens the Mini App at (InitializeIntegrationApp only
  // runs on `/integration-app/<slug>`).
  useSpeedSessionHandshake();

  const groupHasLoaded = group.id !== "";
  const showNavigation = !excludeNavigationPaths.includes(location.pathname);
  const showBackgroundColor =
    !excludeBackgroundColorPaths.includes(location.pathname) && groupHasLoaded;

  const userId = user.id;

  if (group.settings.enable_geolocation && userId) {
    InitializeGeolocation(userId);
  }

  const dispatch = useAppDispatch();

  const isIntegratedAppHomePage = useIsIntegratedAppHomePage();

  // need this condition after leaving home page
  const integratedAppSlice = useAppSelector(getIntegratedAppState);
  const integratedAppState = integratedAppSlice.state;

  // Resolve the initial UNDEFINED state for non-integration flows so downstream
  // code (useContestData, contest-page, etc.) stops waiting on the integration
  // handshake. Integration flows transition the state themselves via
  // InitializeIntegrationApp -> setIntegratedState.
  //
  // Cold-load race: on first mount of `/integration-app/<slug>/...?acct=...`,
  // IS_INTEGRATED_APP isn't in sessionStorage yet — it's only written when
  // setIntegratedState fires from InitializeIntegrationApp's useEffect (which
  // runs AFTER App-level effects, since it lives in the <HomePage> subtree).
  // Without the extra guards below this effect would dispatch
  // removeIntegratedState() during that window, wiping SPEED_ACCOUNT_ID /
  // TOKEN out of sessionStorage and flipping the state to NOT_INTEGRATED.
  // Downstream subscribers (DepositFundsModal, OrdersManager, fetchOrders,
  // etc.) react to NOT_INTEGRATED and start the non-integration flow before
  // the Speed handshake completes — surfaces as "deposit/profile page won't
  // open" on Speed Wallet Mini App launches.
  //
  // Treat the session as integration-bound (and skip the reset) if ANY of:
  //   - we're in an iframe / WebView (parent may be Speed Wallet),
  //   - the URL path is /integration-app/...,
  //   - the URL still carries the `acct` handshake param,
  //   - useSpeedSessionHandshake just wrote SPEED_ACCOUNT_ID to sessionStorage,
  //   - IS_INTEGRATED_APP is already "true" (warm reload / in-tab nav).
  useEffect(() => {
    if (integratedAppState !== IntegratedAppState.UNDEFINED) return;

    const embeddedInIframe = window !== window.top;
    const onIntegrationPath = location.pathname.includes(
      INTEGRATED_APP_HOME_PAGE_URL_PREFIX
    );
    const queryParams = new URLSearchParams(location.search);
    const hasAcctInUrl = queryParams.has("acct");
    // Non-Speed providers (penn, soboba, ...) hand off via `login_token` /
    // `loginToken` in the URL instead of `acct`. Without this carve-out the
    // reset below wipes TOKEN/USER_ID from sessionStorage during the handshake
    // window, so in-flight balance requests go out with empty auth headers and
    // the backend rejects them 401 — surfacing as repeated "Unable to fetch
    // balance" toasts on penn launches.
    const hasLoginTokenInUrl =
      queryParams.has("login_token") || queryParams.has("loginToken");
    const hasSpeedSession = !!storage.session.getItem(SPEED_ACCOUNT_ID);
    const hasIntegratedFlag =
      storage.session.getItem(IS_INTEGRATED_APP) === "true";

    if (
      embeddedInIframe ||
      onIntegrationPath ||
      hasAcctInUrl ||
      hasLoginTokenInUrl ||
      hasSpeedSession ||
      hasIntegratedFlag
    ) {
      return;
    }
    dispatch(removeIntegratedState());
  }, [dispatch, integratedAppState, location.pathname, location.search]);

  useEffect(() => {
    if (
      (integratedAppState === IntegratedAppState.NOT_INTEGRATED &&
        user.id &&
        auth.signedIn) ||
      integratedAppState === IntegratedAppState.INTEGRATED_SESSION_INITIALIZED
    ) {
      const userPath =
        integratedAppState === IntegratedAppState.NOT_INTEGRATED
          ? getUserPath()
          : integratedUserPath();

      getRequest(userPath)
        .then((user: UsersResponse) =>
          dispatch(setSuperAdmin({ super_admin: user.super_admin }))
        )
        .catch((error) => console.error("Failed to fetch user:", error));

      if (integratedAppSlice.speed?.accountId) {
        dispatch(fetchUser());
      }
    }
  }, [integratedAppState, user.id, auth.signedIn, dispatch]);

  if (!integratedAppSlice.isIntegratedApp && !isIntegratedAppHomePage) {
    InitializeGroupState(location.pathname);
    InitializeUserState();
  }

  CheckGroupStatus();

  useEffect(() => {
    ReactGA4.send({
      hitType: "pageview",
      page: location.pathname,
      title: "Page View",
    });
  }, [location]);

  const wagerWireEnabled = !!group.settings.wager_wire;
  const { isMobile } = useDeviceDimensions();

  // Set up global console capture
  useConsoleCapture();

  // Set up global network capture
  useNetworkCapture();

  useEffect(() => {
    dispatch(close());
  }, [dispatch, isMobile]);

  useEffect(() => {
    if (
      auth.signedIn &&
      user.id &&
      integratedAppState === IntegratedAppState.NOT_INTEGRATED
    ) {
      dispatch(fetchPaymentMethods());
      dispatch(fetchRealWallet());
      dispatch(fetchOrders());
      dispatch(fetchProducts());
      dispatch(fetchBattlePasses());
    }
  }, [dispatch, user.id, auth.signedIn, integratedAppState]);

  useEffect(() => {
    if (
      auth.signedIn &&
      user.id &&
      group.id &&
      integratedAppState === IntegratedAppState.NOT_INTEGRATED
    ) {
      dispatch(fetchProducts());
      dispatch(fetchBattlePasses());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user.id, auth.signedIn, group.id]);

  useEffect(() => {
    if (
      integratedAppState === IntegratedAppState.NOT_INTEGRATED &&
      !isIntegratedAppHomePage &&
      groupHasLoaded
    ) {
      dispatch(fetchGroupCustomization(group.id));
    }
  }, [
    dispatch,
    group.id,
    integratedAppState,
    isIntegratedAppHomePage,
    groupHasLoaded,
  ]);

  const handleAppConfigChange = () => {
    if (checkIfAppConfigResetIsRequired()) {
      console.log("resetting app config to defaults", defaultAppConfig);
      dispatch(setAppConfig(defaultAppConfig));
      localStorage.removeItem(APP_CONFIG_OVERRIDES_LOCAL_STORAGE_KEY);
    } else {
      const appConfigOverrides = getAppConfigOverridesFromQueryParams();
      if (Object.keys(appConfigOverrides).length > 0) {
        console.log("applying app config overrides", appConfigOverrides);

        dispatch(setAppConfig(appConfigOverrides));

        const storedOverrides = getAppConfigOverridesFromLocalStorage();
        localStorage.setItem(
          APP_CONFIG_OVERRIDES_LOCAL_STORAGE_KEY,
          JSON.stringify({
            ...storedOverrides,
            ...appConfigOverrides,
          })
        );
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => handleAppConfigChange(), [dispatch, location]);

  const conditionalStyles = isMobile
    ? {}
    : {
        alignSelf: "flex-end",
        height: "100vh",
        overflow: "scroll",
        width: "100%",
      };

  const primaryColor = useAppSelector(getGroupStyles).primary_color;

  const backgoundStyles = showBackgroundColor
    ? {
        backgroundImage: `linear-gradient(to top, ${primaryColor} , ${sparketBlack})`,
      }
    : { backgroundColor: "#F1F2F2" };

  const showLogin = group.customization.sign_in_up;
  const showBankroll = group.customization.bankroll_page;
  const showLeaderboard = group.customization.leaderboard;

  const secondaryColor = useAppSelector(getGroupSecondaryColorOrDefault);
  const root = document.documentElement;
  root.style.setProperty("--secondary-color", secondaryColor);

  const isBlackText = group.customization.black_text;
  root.style.setProperty("--text-color", isBlackText ? "black" : "white");

  const contestGroup = group.contest_group;

  useEffect(() => {
    if (!group.id) {
      return;
    }
    dispatch(setIsContestGroup(contestGroup));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id, contestGroup]);

  const HomePage = contestGroup ? ContestHome : Home;

  return (
    <WagerWireProvider
      userId={
        auth.signedIn && group.status === UserGroupMembershipStatus.APPROVED
          ? userId
          : ""
      }
      embedOrigin={process.env.REACT_APP_WAGER_WIRE_EMBED_ORIGIN || ""}
    >
      <div style={{ display: isMobile ? "initial" : "flex" }}>
        {showNavigation && <>{isMobile ? <Drawer /> : <SideBar />}</>}
        <div
          style={{
            minHeight: "100vh",
            ...backgoundStyles,
            ...conditionalStyles,
          }}
        >
          {(integratedAppState === IntegratedAppState.NOT_INTEGRATED ||
            integratedAppSlice.speed?.accountId) && (
            <OrdersManager />
          )}

          <Routes>
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/plaid-oauth" element={<PlaidOAuthPage />} />
            <Route path="/user/:id" element={<UserPage />} />
            <Route
              path={EDIT_PROFILE_PATH}
              element={<IdentityVerificationPage />}
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/mypicks" element={<MyPicksPage />} />
            <Route path="/bets/:id" element={<MyPicksPage />} />
            <Route path="/pools/:id" element={<PoolPage />} />
            <Route path="/help" element={<Help />} />
            {showBankroll && <Route path="/bankroll" element={<Bankroll />} />}
            {showLeaderboard && (
              <Route path="/leaderboard" element={<LeaderboardPage />} />
            )}
            <Route
              path="/privacy-policy"
              element={<LegalPage url={getPrivacyPolicyPath()} />}
            />
            <Route
              path="/terms-of-service"
              element={<LegalPage url={getTermsOfServicePath()} />}
            />
            <Route
              path="/notification-settings"
              element={<NotificationSettings />}
            />
            <Route path="/confirm-results" element={<ConfirmResults />} />
            <Route
              path="/pools/:id/confirm"
              element={<ConfirmResultsPoolDetails />}
            />
            <Route path="/partner-game" element={<PartnerGame />}></Route>
            <Route
              path="/responsible-gaming"
              element={<ResponsibleGaming />}
            ></Route>
            {user.super_admin && (
              <Route path="/debug" element={<DebugPage />} />
            )}
            <Route path="/" element={<HomePage />} />
            <Route path="/g/:groupSlug" element={<HomePage />} />
            <Route path="/contest/:id" element={<ContestPage />} />
            <Route
              path="/development/ui-library/contest"
              element={<UiLibraryContestPage />}
            />
            <Route
              path={`/${INTEGRATED_APP_HOME_PAGE_URL_PREFIX}/:groupSlug`}
              element={<HomePage />}
            />
            <Route
              path={`/${INTEGRATED_APP_HOME_PAGE_URL_PREFIX}/:groupSlug/launchGame/:gameType`}
              element={<HomePage />}
            />
          </Routes>
          <SparketTutorial />
          <LegalModal />
          <ExternalIdModal />
          {showLogin && <LoginModal />}
          <TwoFactorModal />
          {groupHasLoaded && !group.customization.iframe && (
            <UnsupportedBrowserAlert />
          )}
          <OfflineAlert />
          <ContestPopup />
          <WithdrawFundsModal />
          <DepositFundsModal />
          <GlobalLoadingOverlay />
        </div>
      </div>
      {wagerWireEnabled && <WagerWireIframe />}
    </WagerWireProvider>
  );
};

export default App;
